import React, { useEffect, useState } from 'react'
import throttle from 'lodash.throttle'

import { Skeleton, useForDesktop } from '@sketch/components'
import { useGetNotificationQuery } from '@sketch/gql-types'

import { InboxPreviewActions } from './InboxPreviewActions'
import { InboxPreviewBody } from './InboxPreviewBody'
import { InboxPreviewHeader } from './InboxPreviewHeader'
import { InboxPreviewReason } from './InboxPreviewReason'
import {
  ActionsBar,
  InboxContent,
  ReasonRow,
  Root,
  StickyActions,
  VerticalDivider,
} from './InboxPreview.styles'
import {
  getNotificationConfig,
  resolveNotificationTitle,
} from './notificationConfig'
import { useMarkNotificationAsReadOnOpen } from './useMarkNotificationAsReadOnOpen'
import { Card } from './CommentMentionPreview.styles'

interface InboxPreviewProps {
  notificationId: string
}

const SCROLLED_THRESHOLD_PX = 4
const SCROLL_THROTTLE_MS = 100

const findScrollContainer = (el: HTMLElement): HTMLElement | null => {
  let current: HTMLElement | null = el
  while (current) {
    const overflowY = getComputedStyle(current).overflowY
    if (overflowY === 'auto' || overflowY === 'scroll') return current
    current = current.parentElement
  }
  return null
}

const SkeletonPreview = () => (
  <Root>
    <StickyActions>
      <ActionsBar>
        <Skeleton width="320px" height="32px" />
      </ActionsBar>
    </StickyActions>
    <InboxContent>
      <ReasonRow>
        <Skeleton width="28px" height="28px" />
        <Skeleton width="200px" height="28px" />
      </ReasonRow>
      <VerticalDivider />
      <Card>
        <Skeleton height="200px" />
      </Card>
      <VerticalDivider />
      <ReasonRow>
        <Skeleton width="24px" height="24px" />
        <Skeleton width="200px" height="24px" />
      </ReasonRow>
    </InboxContent>
  </Root>
)

export const InboxPreview = ({ notificationId }: InboxPreviewProps) => {
  const isDesktop = useForDesktop()
  const { data, loading } = useGetNotificationQuery({
    errorPolicy: 'all',
    variables: { identifier: notificationId },
  })

  // Track whether the sticky actions bar is currently overlapping content, so
  // it can fade in its backdrop. On desktop the bar sticks to the top, so the
  // overlap signal is "user has scrolled past the top"; inside the mobile
  // Modal the bar sticks to the bottom, so the signal flips to "there's still
  // content below the bar."
  //
  // A callback ref (rather than useRef) lets the listener effect run exactly
  // when Root mounts/unmounts, instead of re-attaching on every unrelated
  // `notification` cache write (mark-as-read elsewhere, subscription updates)
  // that returns a fresh ref.
  const [root, setRoot] = useState<HTMLElement | null>(null)
  const [isOverlappingContent, setIsOverlappingContent] = useState(false)

  const notification = data?.notification

  // Opening a notification marks it as read, if it isn't already.
  useMarkNotificationAsReadOnOpen(notification)

  useEffect(() => {
    if (!root) return

    // On desktop the preview's own Root scrolls; inside the mobile Modal it
    // doesn't (the modal Container does). Walk up to whichever ancestor is
    // actually the scroll container so the backdrop fade tracks it.
    const scrollContainer = findScrollContainer(root)
    if (!scrollContainer) return

    const handleScroll = throttle(() => {
      if (isDesktop) {
        setIsOverlappingContent(
          scrollContainer.scrollTop > SCROLLED_THRESHOLD_PX
        )
      } else {
        const distanceFromBottom =
          scrollContainer.scrollHeight -
          scrollContainer.clientHeight -
          scrollContainer.scrollTop
        setIsOverlappingContent(distanceFromBottom > SCROLLED_THRESHOLD_PX)
      }
    }, SCROLL_THROTTLE_MS)

    handleScroll()
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      handleScroll.cancel()
      scrollContainer.removeEventListener('scroll', handleScroll)
    }
  }, [root, isDesktop])

  if (loading) return <SkeletonPreview />
  if (!notification) return null

  const config = getNotificationConfig(notification.__typename)

  // On desktop the bar sticks to the top, so it must be the first child of
  // Root for its natural flow position to match. Inside the mobile Modal it
  // sticks to the bottom and must be the last child for the same reason.
  const actionsBar = (
    <InboxPreviewActions
      notification={notification}
      scrolled={isOverlappingContent}
    />
  )

  return (
    <Root ref={setRoot}>
      {isDesktop && actionsBar}

      <InboxContent>
        <InboxPreviewHeader
          icon={config.typeIcon}
          title={resolveNotificationTitle(config.title, notification)}
        />
        <VerticalDivider />
        <InboxPreviewBody notification={notification} />
        <VerticalDivider />
        <InboxPreviewReason reason={config.reason} />
      </InboxContent>

      {!isDesktop && actionsBar}
    </Root>
  )
}
